import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
  noIndex?: boolean;
  playlist?: {
    name: string;
    curator_name: string | null;
    followers: number;
    updated_at: string;
    genre?: string;
    genre_count?: number;
  };
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
  };
  structuredData?: any;
}

export function SEO({ 
  title, 
  description,
  image = "https://ftwlzdhkzqfuosdorgjt.supabase.co/storage/v1/object/public/playlistpartner%20public/playlistpartner%20og%20image.png",
  url,
  type = "website",
  noIndex = false,
  playlist,
  article,
  structuredData
}: SEOProps) {
  const siteTitle = playlist 
    ? `Submit Music to ${playlist.name} - Free Spotify Playlist Submission`
    : title || "Free Spotify Playlist Submission – No Signup | PlaylistPartner";
  const defaultDescription = playlist 
    ? `Get your music featured on ${playlist.name}, a Spotify playlist curated by ${playlist.curator_name} with ${playlist.followers.toLocaleString()} followers. No account needed! Access ${playlist.genre_count?.toLocaleString() || 'thousands of'} ${playlist.genre || 'playlists'} and connect with curators on PlaylistPartner.com.`
    : "Submit your music to thousands of independent Spotify playlist curators for free. No signup required—connect with curators and grow your audience today with PlaylistPartner.";
  const metaDescription = description || defaultDescription;
  const canonicalUrl = url || window.location.href.split('?')[0];
  const rootUrl = window.location.origin;

  return (
    <Helmet>
      {/* Basic tags */}
      <title>{siteTitle}</title>
      <meta name="description" content={metaDescription} data-react-helmet="true" />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      <meta 
        name="robots" 
        content={noIndex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"} 
      />

      {/* OpenGraph */}
      <meta property="og:site_name" content="PlaylistPartner" />
      <meta property="og:title" content={siteTitle} data-react-helmet="true" />
      <meta property="og:description" content={metaDescription} data-react-helmet="true" />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} data-react-helmet="true" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={siteTitle} data-react-helmet="true" />
      <meta name="twitter:description" content={metaDescription} data-react-helmet="true" />
      <meta name="twitter:image" content={image} data-react-helmet="true" />
      <meta name="twitter:site" content="@playlistpartner" />

      {/* Article specific meta tags */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          {article.modifiedTime && <meta property="article:modified_time" content={article.modifiedTime} />}
          {article.author && <meta property="article:author" content={article.author} />}
          {article.section && <meta property="article:section" content={article.section} />}
        </>
      )}

      {/* Schema.org */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || {
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "PlaylistPartner",
          "url": rootUrl,
          "inLanguage": "en-US",
          "description": "Submit your music to thousands of independent Spotify playlist curators for free. No signup required—connect with curators and grow your audience today with PlaylistPartner.",
          "publisher": {
            "@type": "Organization",
            "name": "PlaylistPartner",
            "logo": {
              "@type": "ImageObject",
              "url": "https://ftwlzdhkzqfuosdorgjt.supabase.co/storage/v1/object/public/playlistpartner%20public//playlist%20partner%20logo%20square.png",
              "width": "500",
              "height": "500"
            }
          },
          "potentialAction": {
            "@type": "ViewAction",
            "target": canonicalUrl
          }
        })}
      </script>
    </Helmet>
  );
}